/**
 * Pilot content component.
 */
import EpgEvent from '@shared/classes/epgEvent';
import Footer from '@components/atoms/container/pilotFooterContainer';
import PilotAppLogo from '@components/atoms/images/pilotAppLogo';
import PilotEvent from '@components/elements/pilot/pilotEvent';
import PilotInfo from '@components/elements/pilot/pilotInfo';
import PilotPoster from '@components/elements/pilot/pilotPoster';
import PilotProgressBar from '@components/elements/pilot/pilotProgressBar';
import PilotTip from '@components/elements/pilot/pilotTip';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { PilotFooterProps as Props } from '@shared/types/props/pilot';
import { playerSelector } from '@shared/store/slices/player.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useMemo } from 'react';

/**
 * Renders the pilot content component.
 * Displays the pilot event, progress bar, info, tip, poster and app logo.
 */
export default function PilotContent(parentProps: Props) {
  const { display, props } = usePilotContent(parentProps);
  return display ? (
    <>
      <Footer>
        <PilotEvent {...props.event} />
        <PilotProgressBar {...props.progressBar} />
        <PilotInfo {...props.info} />
        <PilotTip />
        <PilotPoster {...props.poster} />
        <PilotAppLogo />
      </Footer>
    </>
  ) : null;
}

/**
 * Hook to get the pilot content data.
 * @param {Props} parentProps - The parent props.
 * @returns {Object} The pilot content data.
 */
function usePilotContent({ currentChannel, currentEvent, type }: Props) {
  // Destructure event
  const {
    ageCode,
    eventId,
    poster,
    score: rating,
    title: eventTitle,
  } = currentEvent || {};

  // Destructure channel
  const {
    id: channelId,
    logo: channelLogo,
    title: channelTitle,
  } = currentChannel || {};

  // Retrieve player data
  const {
    nextEpisode: { display: displayNextEpisode },
    pilot: displayPilot,
  } = useAppSelector(playerSelector);
  // Memoize the pilot event title
  const pilotEventTitle = useMemo(() => {
    const { episodeNumber, seasonNumber, episodeTitle } = currentEvent || {};
    const literals = {
      episode: episodeNumber
        ? `${TranslationManager.get(
            'MISC.EVENTS.EPISODE_FIRST_LETTER'
          )}${episodeNumber}`
        : '',
      season: seasonNumber
        ? `${TranslationManager.get(
            'MISC.EVENTS.SEASON_FIRST_LETTER'
          )}${seasonNumber}`
        : '',
      title: episodeTitle ? `${episodeTitle}` : '',
    };
    const separator = {
      initial: ' · ',
      slash: ' | ',
    };
    const titleInfo = [...Object.values(literals)]
      .filter((item) => item.length > 0)
      .join(separator.slash);
    return `${eventTitle ?? ''}${
      titleInfo ? separator.initial : ''
    }${titleInfo}`;
  }, [eventTitle]);

  // Memoize and return the pilot content data
  return useMemo(() => {
    return {
      display: displayNextEpisode || displayPilot,
      props: {
        event: {
          id: channelId,
          logo: channelLogo,
          subtitle: pilotEventTitle,
          title: channelTitle,
          type,
        },
        info: {
          ageCode,
          rating,
        },
        poster: {
          id: eventId,
          poster,
          lowPoster: (currentEvent as EpgEvent)?.lowPoster || '',
        },
        progressBar: {
          display: displayPilot,
          event: currentEvent,
          type,
        },
      },
    };
  }, [currentChannel, currentEvent, type, pilotEventTitle, displayPilot]);
}
